import { Sun, Moon } from '@phosphor-icons/react';
import { useTheme } from '@/context/theme';

/**
 * Binary light/dark theme toggle.
 *
 * Presentational: reads `resolvedTheme` from `useTheme()` and renders a single
 * Phosphor icon indicating the action:
 *   - dark  → <Sun />  (click switches to light)
 *   - light → <Moon /> (click switches to dark)
 *
 * Transition policy (instant switch, no smear): before flipping the theme, add
 * `theme-transitioning` to <html>. That class carries a one-frame
 * `* { transition: none !important }` rule (in index.css) which suppresses
 * every element's transition while the `data-theme` attribute changes — so the
 * switch is instantaneous rather than a smeared multi-property animation. We
 * remove the class on the next frame (double rAF) so transitions resume.
 */
export function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const handleToggle = () => {
    const root = document.documentElement;

    root.classList.add('theme-transitioning');
    setTheme(isDark ? 'light' : 'dark');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.remove('theme-transitioning');
      });
    });
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      className="text-text/40 hover:text-text/80 transition-colors"
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? <Sun size={15} /> : <Moon size={15} />}
    </button>
  );
}
